(() => {
  const TIME_ZONE = 'Europe/Chisinau';
  const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const REFRESH_MS = 60000;

  const parseInterval = (raw) => {
    if (!raw) return null;
    const match = raw.trim().match(/(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const open = Number(match[1]) * 60 + Number(match[2]);
    let close = Number(match[3]) * 60 + Number(match[4]);
    if (close <= open) close += 1440;
    return { open, close };
  };

  const formatTime = (minutes) => {
    const total = ((minutes % 1440) + 1440) % 1440;
    const hours = String(Math.floor(total / 60)).padStart(2, '0');
    const mins = String(total % 60).padStart(2, '0');
    return `${hours}:${mins}`;
  };

  const localNow = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIME_ZONE,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const read = (type) => parts.find((part) => part.type === type)?.value ?? '';
    const hour = Number(read('hour')) % 24;

    return { day: WEEKDAY_INDEX[read('weekday')] ?? 0, minutes: hour * 60 + Number(read('minute')) };
  };

  class SalutLocations extends HTMLElement {
    connectedCallback() {
      this.labels = this.readLabels();
      this.threshold = Number(this.dataset.closingSoon) || 30;
      this.rows = Array.from(this.querySelectorAll('[data-row]'));
      this.list = this.querySelector('[data-list]');
      this.error = this.querySelector('[data-error]');
      this.input = this.querySelector('[data-search]');
      this.clear = this.querySelector('[data-clear]');
      this.searchBox = this.clear?.closest('.salut-locations__search');
      this.filterButtons = Array.from(this.querySelectorAll('[data-filter]'));
      this.viewButtons = Array.from(this.querySelectorAll('[data-view]'));
      this.filter = 'all';
      this.query = '';

      this.rows.forEach((row) => {
        row.schedule = {
          1: parseInterval(row.dataset.weekdays),
          6: parseInterval(row.dataset.saturday),
          0: parseInterval(row.dataset.sunday),
        };
      });

      this.bind();
      this.paintStatuses();
      this.apply();
      this.timer = window.setInterval(() => this.paintStatuses(), REFRESH_MS);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    readLabels() {
      const holder = this.querySelector('[data-salut-locations-labels]');
      try {
        return JSON.parse(holder?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    bind() {
      this.input?.addEventListener('input', () => {
        this.query = this.input.value.trim().toLowerCase();
        this.searchBox?.classList.toggle('salut-locations__search--filled', this.query !== '');
        if (this.clear) this.clear.hidden = this.query === '';
        this.apply();
      });

      this.clear?.addEventListener('click', () => {
        if (!this.input) return;
        this.input.value = '';
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      });

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.filter = button.dataset.filter;
          this.filterButtons.forEach((other) => {
            const active = other === button;
            other.classList.toggle('salut-locations__chip--active', active);
            other.setAttribute('aria-pressed', String(active));
          });
          this.apply();
        });
      });

      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setView(button.dataset.view));
      });

      this.querySelectorAll('[data-map-cta]').forEach((button) => {
        button.addEventListener('click', () => this.setView('map'));
      });
    }

    setView(view) {
      this.dataset.view = view;
      this.viewButtons.forEach((button) => {
        const active = button.dataset.view === view;
        button.classList.toggle('salut-locations__chip--active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    intervalFor(row, day) {
      if (day === 0 || day === 6) return row.schedule[day];
      return row.schedule[1];
    }

    statusFor(row, now) {
      const today = this.intervalFor(row, now.day);
      const yesterday = this.intervalFor(row, (now.day + 6) % 7);

      if (yesterday && yesterday.close > 1440 && now.minutes < yesterday.close - 1440) {
        return this.openState(yesterday.close - 1440, now.minutes);
      }

      if (today && now.minutes >= today.open && now.minutes < today.close) {
        return this.openState(today.close, now.minutes);
      }

      if (today && now.minutes < today.open) {
        return this.closedState(this.labels.opens_today, today.open);
      }

      for (let step = 1; step <= 7; step += 1) {
        const day = (now.day + step) % 7;
        const interval = this.intervalFor(row, day);
        if (!interval) continue;
        if (step === 1) return this.closedState(this.labels.opens_tomorrow, interval.open);
        return this.closedState(this.labels.opens_on, interval.open, this.labels.days?.[day]);
      }

      return { modifier: 'closed', state: this.labels.closed || '', note: '' };
    }

    openState(close, minutes) {
      const left = close - minutes;
      if (left <= this.threshold) {
        return {
          modifier: 'soon',
          state: (this.labels.closing_soon || '').replace('__minutes__', String(Math.max(left, 1))),
          note: '',
        };
      }
      return {
        modifier: 'open',
        state: this.labels.open_now || '',
        note: ` ${(this.labels.open_until || '').replace('__time__', formatTime(close))}`,
      };
    }

    closedState(template, open, day) {
      const note = (template || '')
        .replace('__time__', formatTime(open))
        .replace('__day__', day || '');
      return { modifier: 'closed', state: this.labels.closed || '', note: ` · ${note}` };
    }

    paintStatuses() {
      const now = localNow();
      this.rows.forEach((row) => {
        const target = row.querySelector('[data-status]');
        if (!target) return;
        const status = this.statusFor(row, now);
        target.className = `salut-locations__status salut-locations__status--${status.modifier}`;
        target.innerHTML = '';
        const state = document.createElement('span');
        state.className = 'salut-locations__status-state';
        state.textContent = status.state;
        target.appendChild(state);
        if (status.note) {
          const note = document.createElement('span');
          note.className = 'salut-locations__status-note';
          note.textContent = status.note;
          target.appendChild(note);
        }
      });
    }

    matches(row) {
      if (this.filter === 'weekend' && row.dataset.weekend !== 'true') return false;
      if (this.filter === 'extended' && row.dataset.extended !== 'true') return false;
      if (this.query && !row.dataset.search.includes(this.query)) return false;
      return true;
    }

    apply() {
      let visible = null;
      this.rows.forEach((row) => {
        const shown = this.matches(row);
        row.hidden = !shown;
        row.removeAttribute('data-last');
        if (shown) visible = row;
      });

      if (visible) visible.setAttribute('data-last', '');
      if (this.list) this.list.hidden = visible === null;
      if (this.error) this.error.hidden = visible !== null || this.rows.length === 0;
    }
  }

  if (!customElements.get('salut-locations')) {
    customElements.define('salut-locations', SalutLocations);
  }
})();
